import React, { useState } from 'react';

import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import { RTW_FAQ_LINK } from 'src/config/ExternalLinks';
import { type TaskConflict } from 'src/services/jmr/types';
import { type Modification } from 'src/types/modification';
import { createDataCyForJob, type TableDataRow } from 'src/utils/jmrUtils';
import { getRequirementColor, getRequirementQuantity } from 'src/utils/placementSelectionUtils';

interface ApplyModificationsModalProps {
  jobToModify: TableDataRow | null;
  isOpen: boolean;
  onCancel: () => void;
  onSaveAndApply: (modifiedConflicts: TaskConflict[]) => void;
  exceededRestrictionIds: Set<number>;
}
const ApplyModificationsModal = ({ jobToModify, isOpen, onCancel, ...contentProps }: ApplyModificationsModalProps) => {
  return (
    <Modal
      title={`Modify: ${jobToModify?.name}`}
      open={isOpen}
      onClose={onCancel}
      closeLabel="Close"
      aria-describedby="modal-description"
      width="700px"
      scrollContainer="modal"
    >
      <ApplyModificationsModalContent {...{ jobToModify, onCancel, ...contentProps }} />
    </Modal>
  );
};

const ApplyModificationsModalContent = ({
  jobToModify,
  onCancel,
  onSaveAndApply,
  exceededRestrictionIds,
}: Omit<ApplyModificationsModalProps, 'isOpen'>) => {
  const [checkboxState, setCheckboxState] = useState<Record<string, boolean>>({});

  const isValid = () => jobToModify?.conflicts!.every((conflict) => hasBeenModified(conflict));

  const hasBeenModified = (conflict: TaskConflict) =>
    conflict.availableModifications.some((modification) => checkboxState[hashCheckbox(conflict, modification)]);

  const hashCheckbox = ({ taskId }: TaskConflict, { id }: Modification) => `${taskId}-${id}`;

  /** closure capturing exceededRestrictionIds for brevity in tsx attribute */
  const _getRequirementColor = (conflict: TaskConflict) => getRequirementColor(conflict, exceededRestrictionIds);

  const getModifiedConflicts = () =>
    jobToModify!.conflicts.map((conflict) => ({
      ...conflict,
      appliedModifications: conflict.availableModifications.filter(
        (modification) => checkboxState[hashCheckbox(conflict, modification)],
      ),
    }));
  return (
    <>
      <div>
        <Text type="b300">
          Please make the necessary modifications below to make the job an eligible match for the associate. Modifying a
          job will apply a 180 day limit to the job placement. Missing modification? Please submit a{' '}
          <Link target="_blank" href={RTW_FAQ_LINK}>
            SIM ticket
          </Link>
          .
        </Text>
      </div>
      {!!jobToModify && (
        <Column spacingInset={'400'}>
          {jobToModify.conflicts.map((conflict) => (
            <Box key={conflict.taskId} spacingInset={'400'} type="outline">
              <Row alignmentHorizontal="justify">
                <Row spacing="200">
                  <Text color={_getRequirementColor(conflict)} type="h100">
                    Requirement: {conflict.taskName}
                  </Text>
                  {conflict.requirementProportion && (
                    <Text color={_getRequirementColor(conflict)}>
                      ({getRequirementQuantity(jobToModify, conflict)} of shift)
                    </Text>
                  )}
                </Row>
                <Tag type={hasBeenModified(conflict) ? 'success' : 'warning'}>
                  {hasBeenModified(conflict) ? 'Modified' : 'Conflict'}
                </Tag>
              </Row>
              <Text>
                <strong>Conflict: </strong>
                {`Associate cannot ${conflict.taskName}${conflict.restrictedValue ? ` (${conflict.restrictedValue} Hours)` : ''}`}
              </Text>
              <Column spacing={'200'} spacingInset={'400 none none none'}>
                <Text type="h100">Select a Modification</Text>
                {conflict.availableModifications?.map((modification, i) => (
                  <Row key={i}>
                    <Checkbox
                      checked={checkboxState[hashCheckbox(conflict, modification)]}
                      aria-label={modification.name}
                      onChange={(isChecked) =>
                        setCheckboxState({ ...checkboxState, [hashCheckbox(conflict, modification)]: isChecked })
                      }
                      data-cy={`${createDataCyForJob(jobToModify.name)}-modification-${i}`}
                    />
                    <Text type="b300">{modification.name}</Text>
                  </Row>
                ))}
              </Column>
            </Box>
          ))}
        </Column>
      )}
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button type="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            data-cy={'new-jmr__results__apply-modifications'}
            type="primary"
            disabled={!isValid()}
            onClick={() => onSaveAndApply(getModifiedConflicts())}
          >
            Apply & Select
          </Button>
        </Row>
      </ModalFooter>
    </>
  );
};

export default ApplyModificationsModal;
